/**
 * Image Generator Submit Hook
 *
 * Handles image generation submission, WebSocket streaming,
 * polling fallback, download, and delete operations.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { api, ModelDefinitionListItem, WEBSOCKET_ENDPOINT } from '@/lib/api';
import { authApi } from '@/lib/auth-api';
import { useStreaming } from '@/lib/hooks';
import { traceStart, traceEnd, traceError, addBreadcrumb } from '@/lib/telemetry';
import { getSelectedVaultHolderId } from '@/components/HolderSwitcher';
import { toast } from '@/components/ui/use-toast';
import { ImageGenerationStage } from '../components/ImageGenerationProgress';
import {
  EnhancementKey,
  GeneratedImage,
  ImageInstanceConfig,
  ProviderParams,
  ReferenceImage,
  CreativeStyleChoice,
  ENHANCEMENT_CATEGORIES,
  ARCHE_ID,
  NUM_INSTANCES,
  filterProviderParamsForModel,
  extractQualityStyleFromParams,
  getActiveModelSettings,
  getProviderParamsForModel,
} from '../types/image-generator.types';
import type { ImageGeneratorState } from './useImageGeneratorState';

/** Maximum number of polling attempts before giving up (2s interval = 5 min) */
const MAX_POLL_ATTEMPTS = 150;

// ============================================================================
// Types
// ============================================================================

export interface UseImageGeneratorSubmitOptions {
  state: ImageGeneratorState;
  updateInstanceState: (
    instanceId: number,
    updates: {
      stage: ImageGenerationStage;
      progressMessage: string;
      currentRequestId?: string | null;
      error?: string | null;
    },
  ) => void;
  addImage: (instanceId: number, image: GeneratedImage) => void;
  removeImage: (instanceId: number, requestId: string) => void;
  getModelDisplayName: (modelName: string) => string;
  availableModels?: ModelDefinitionListItem[];
}

export interface UseImageGeneratorSubmitResult {
  handleSubmit: (inputValue: string) => Promise<void>;
  handleCancel: () => void;
  handleCancelInstance: (instanceId: number) => void;
  handleDownload: (image: GeneratedImage) => Promise<void>;
  handleDelete: (requestId: string, instanceId: number) => void;
  isAnyGenerating: boolean;
  isInstanceGenerating: (idx: number) => boolean;
  scrollAllInstancesToBottom: () => void;
  instanceScrollRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
}

/**
 * What a panel actually generated with, frozen at submit time.
 *
 * The pending-request map used to hold the whole live `ImageInstanceConfig`,
 * which meant a settings edit while a generation was in flight could rewrite the
 * metadata recorded against the finished image. Flattening the per-model entry
 * once, here, both freezes it and gives the two completion paths (WebSocket and
 * poll) one shape to read.
 */
export interface GenerationSnapshot {
  provider: string;
  modelName: string;
  size: string;
  quality: string;
  style: string;
  enhancements: Record<EnhancementKey, string>;
  providerParams: ProviderParams;
  referenceImage?: ReferenceImage | null;
  creativeStyle?: CreativeStyleChoice | null;
}

export function toGenerationSnapshot(config: ImageInstanceConfig): GenerationSnapshot {
  const settings = getActiveModelSettings(config);
  return {
    provider: config.provider,
    modelName: config.modelName,
    size: settings.size,
    quality: settings.quality,
    style: settings.style,
    enhancements: { ...config.enhancements },
    providerParams: { ...settings.providerParams },
    referenceImage: config.referenceImage ?? null,
    creativeStyle: config.creativeStyle ?? null,
  };
}

// ============================================================================
// Hook
// ============================================================================

export function useImageGeneratorSubmit(
  options: UseImageGeneratorSubmitOptions,
): UseImageGeneratorSubmitResult {
  const {
    state,
    updateInstanceState,
    addImage,
    removeImage,
    getModelDisplayName,
    availableModels,
  } = options;
  const { instanceConfigs, instanceStates, enabledInstances, minimizedInstances, instanceImages } =
    state;

  // Get auth token lazily so it's always fresh (tokens can expire/refresh)
  const getAuthToken = useCallback(() => authApi.getIdToken(), []);

  // Refs for instance scroll containers
  const instanceScrollRefs = useRef<(HTMLDivElement | null)[]>(
    Array.from({ length: NUM_INSTANCES }, () => null),
  );

  const scrollAllInstancesToBottom = useCallback(() => {
    instanceScrollRefs.current.forEach((ref) => {
      if (ref) {
        ref.scrollTo({ top: ref.scrollHeight, behavior: 'smooth' });
      }
    });
  }, []);

  // Track pending requests: requestId -> { instanceId, prompt, settings }
  const pendingRequestsRef = useRef<
    Map<
      string,
      {
        instanceId: number;
        prompt: string;
        settings: GenerationSnapshot;
        /** The workspace it was generated in — its image is saved there. */
        actingHolderId: string | null;
      }
    >
  >(new Map());

  // Track polling attempts per request to enforce timeout
  const pollAttemptsRef = useRef<Map<string, number>>(new Map());

  // Keep a ref to latest state so polling interval always reads fresh values
  const instanceStatesRef = useRef(instanceStates);
  instanceStatesRef.current = instanceStates;
  const instanceConfigsRef = useRef(instanceConfigs);
  instanceConfigsRef.current = instanceConfigs;

  // Find instance by requestId
  const findInstanceByRequestId = useCallback(
    (requestId: string): number | null => {
      const pending = pendingRequestsRef.current.get(requestId);
      if (pending) return pending.instanceId;
      const idx = instanceStates.findIndex((s) => s.currentRequestId === requestId);
      return idx >= 0 ? idx : null;
    },
    [instanceStates],
  );

  // Handle errors from WebSocket (e.g. generation failures)
  const handleStreamingError = useCallback(
    (instanceId: number, code: string, message: string) => {
      addBreadcrumb('image', `WebSocket error for instance ${instanceId}: [${code}] ${message}`);
      const requestId = instanceStates[instanceId]?.currentRequestId;
      if (requestId) {
        pendingRequestsRef.current.delete(requestId);
        pollAttemptsRef.current.delete(requestId);
      }
      updateInstanceState(instanceId, {
        stage: 'error',
        progressMessage: '',
        error: message || 'Image generation failed',
        currentRequestId: null,
      });
    },
    [instanceStates, updateInstanceState],
  );

  // Handle progress updates from WebSocket
  const handleProgress = useCallback(
    (stage: string, message: string, requestId?: string) => {
      addBreadcrumb(
        'image',
        `Progress: stage=${stage}, message=${message}, requestId=${requestId}`,
      );
      const instanceId = requestId ? findInstanceByRequestId(requestId) : null;
      if (instanceId !== null) {
        const modelName = getModelDisplayName(instanceConfigs[instanceId]?.modelName || 'AI');
        const updatedMessage = message.replace(/\bAI\b/g, modelName);
        updateInstanceState(instanceId, {
          stage: stage as ImageGenerationStage,
          progressMessage: updatedMessage,
        });
      }
    },
    [findInstanceByRequestId, updateInstanceState, instanceConfigs, getModelDisplayName],
  );

  // Handle image completion from WebSocket
  const handleImageComplete = useCallback(
    (data: {
      requestId: string;
      imageRef: string;
      revisedPrompt?: string;
      provider: string;
      model: string;
      /** Credits this generation charged (1 credit = 1¢); from the arche route. */
      costCents?: number;
      /** Saved to Personal: the user can't save in the workspace they act in. */
      savedToPersonal?: boolean;
    }) => {
      addBreadcrumb('image', `Image complete: imageRef=${data.imageRef}`);
      if (data.savedToPersonal) {
        toast({
          title: 'Saved to your personal Arche Saves',
          description: 'You can’t save files in the workspace you’re working in.',
        });
      }

      const pendingInfo = pendingRequestsRef.current.get(data.requestId);
      const instanceId = pendingInfo?.instanceId ?? findInstanceByRequestId(data.requestId);

      if (instanceId === null) {
        console.error('Could not find instance for requestId:', data.requestId);
        return;
      }

      // Duplicate check
      if (instanceImages[instanceId].some((img) => img.requestId === data.requestId)) {
        console.debug('Skipping duplicate image delivery', { requestId: data.requestId });
        return;
      }

      const capturedPrompt = pendingInfo?.prompt || '';
      const capturedSettings =
        pendingInfo?.settings ?? toGenerationSnapshot(instanceConfigs[instanceId]);

      api
        .getImageUrl(ARCHE_ID, data.imageRef)
        .then((url) => {
          const newImage: GeneratedImage = {
            requestId: data.requestId,
            imageRef: data.imageRef,
            imageUrl: url,
            prompt: capturedPrompt,
            revisedPrompt: data.revisedPrompt,
            provider: data.provider,
            model: data.model,
            size: capturedSettings.size,
            quality: capturedSettings.quality,
            style: capturedSettings.style,
            enhancements: capturedSettings.enhancements,
            generatedAt: new Date(),
            instanceId,
            usedReferenceImage: !!capturedSettings.referenceImage,
            creativityPercent: capturedSettings.referenceImage
              ? Math.round((1 - (capturedSettings.referenceImage.strength ?? 0.5)) * 100)
              : undefined,
            costCredits: data.costCents,
          };
          addImage(instanceId, newImage);
          setTimeout(scrollAllInstancesToBottom, 100);
        })
        .catch((err) => {
          console.error('Failed to get presigned URL:', err);
          const newImage: GeneratedImage = {
            requestId: data.requestId,
            imageRef: data.imageRef,
            imageUrl: '',
            prompt: capturedPrompt,
            revisedPrompt: data.revisedPrompt,
            provider: data.provider,
            model: data.model,
            size: capturedSettings.size,
            quality: capturedSettings.quality,
            style: capturedSettings.style,
            enhancements: capturedSettings.enhancements,
            generatedAt: new Date(),
            instanceId,
            usedReferenceImage: !!capturedSettings.referenceImage,
            creativityPercent: capturedSettings.referenceImage
              ? Math.round((1 - (capturedSettings.referenceImage.strength ?? 0.5)) * 100)
              : undefined,
            costCredits: data.costCents,
          };
          addImage(instanceId, newImage);
          setTimeout(scrollAllInstancesToBottom, 100);
        })
        .finally(() => {
          // Defer "complete" state until presigned URL is resolved (or failed)
          updateInstanceState(instanceId, {
            stage: 'complete',
            progressMessage: 'Image generated successfully!',
            currentRequestId: null,
          });
          pendingRequestsRef.current.delete(data.requestId);
        });
    },
    [
      findInstanceByRequestId,
      instanceConfigs,
      instanceImages,
      scrollAllInstancesToBottom,
      updateInstanceState,
      addImage,
    ],
  );

  // WebSocket streaming for each instance
  const instance0RequestId = instanceStates[0]?.currentRequestId || null;
  const instance1RequestId = instanceStates[1]?.currentRequestId || null;
  const instance2RequestId = instanceStates[2]?.currentRequestId || null;
  const instance3RequestId = instanceStates[3]?.currentRequestId || null;
  const instance4RequestId = instanceStates[4]?.currentRequestId || null;

  const { isComplete: streaming0Complete, reset: resetStreaming0 } = useStreaming({
    requestId: instance0RequestId,
    authToken: getAuthToken(),
    endpoint: WEBSOCKET_ENDPOINT,
    enabled: !!instance0RequestId,
    onProgress: (stage, message) => handleProgress(stage, message, instance0RequestId || undefined),
    onImageComplete: handleImageComplete,
    onError: (code, message) => handleStreamingError(0, code, message),
  });

  const { isComplete: streaming1Complete, reset: resetStreaming1 } = useStreaming({
    requestId: instance1RequestId,
    authToken: getAuthToken(),
    endpoint: WEBSOCKET_ENDPOINT,
    enabled: !!instance1RequestId,
    onProgress: (stage, message) => handleProgress(stage, message, instance1RequestId || undefined),
    onImageComplete: handleImageComplete,
    onError: (code, message) => handleStreamingError(1, code, message),
  });

  const { isComplete: streaming2Complete, reset: resetStreaming2 } = useStreaming({
    requestId: instance2RequestId,
    authToken: getAuthToken(),
    endpoint: WEBSOCKET_ENDPOINT,
    enabled: !!instance2RequestId,
    onProgress: (stage, message) => handleProgress(stage, message, instance2RequestId || undefined),
    onImageComplete: handleImageComplete,
    onError: (code, message) => handleStreamingError(2, code, message),
  });

  const { isComplete: streaming3Complete, reset: resetStreaming3 } = useStreaming({
    requestId: instance3RequestId,
    authToken: getAuthToken(),
    endpoint: WEBSOCKET_ENDPOINT,
    enabled: !!instance3RequestId,
    onProgress: (stage, message) => handleProgress(stage, message, instance3RequestId || undefined),
    onImageComplete: handleImageComplete,
    onError: (code, message) => handleStreamingError(3, code, message),
  });

  const { isComplete: streaming4Complete, reset: resetStreaming4 } = useStreaming({
    requestId: instance4RequestId,
    authToken: getAuthToken(),
    endpoint: WEBSOCKET_ENDPOINT,
    enabled: !!instance4RequestId,
    onProgress: (stage, message) => handleProgress(stage, message, instance4RequestId || undefined),
    onImageComplete: handleImageComplete,
    onError: (code, message) => handleStreamingError(4, code, message),
  });

  // Polling fallback — reads instanceStates from ref to avoid stale closures
  const hasActiveRequests = instanceStates.some(
    (s) =>
      s.currentRequestId && s.stage !== 'complete' && s.stage !== 'error' && s.stage !== 'idle',
  );

  useEffect(() => {
    if (!hasActiveRequests) return;

    const pollInterval = setInterval(async () => {
      const currentStates = instanceStatesRef.current;
      const activeRequests = currentStates
        .map((s, idx) => ({ requestId: s.currentRequestId, instanceId: idx, stage: s.stage }))
        .filter(
          (r) => r.requestId && r.stage !== 'complete' && r.stage !== 'error' && r.stage !== 'idle',
        );

      for (const { requestId, instanceId } of activeRequests) {
        if (!requestId) continue;

        // Track and enforce polling timeout
        const attempts = (pollAttemptsRef.current.get(requestId) || 0) + 1;
        pollAttemptsRef.current.set(requestId, attempts);

        if (attempts > MAX_POLL_ATTEMPTS) {
          addBreadcrumb('image', `Polling timed out for ${requestId} after ${attempts} attempts`);
          updateInstanceState(instanceId, {
            stage: 'error',
            progressMessage: '',
            error: 'Image generation timed out. Please try again.',
            currentRequestId: null,
          });
          pendingRequestsRef.current.delete(requestId);
          pollAttemptsRef.current.delete(requestId);
          continue;
        }

        try {
          addBreadcrumb('image', `Polling for status: ${requestId} (instance ${instanceId})`);
          const result = await api.checkImageStatus(
            ARCHE_ID,
            requestId,
            pendingRequestsRef.current.get(requestId)?.actingHolderId,
          );

          if (result.status === 'error') {
            addBreadcrumb('image', `Poll detected error for ${requestId}: ${result.error}`);
            updateInstanceState(instanceId, {
              stage: 'error',
              progressMessage: '',
              error: result.error || 'Image generation failed',
              currentRequestId: null,
            });
            pendingRequestsRef.current.delete(requestId);
            pollAttemptsRef.current.delete(requestId);
            continue;
          }

          if (result.status === 'complete' && result.url && result.imageRef) {
            addBreadcrumb('image', `Poll found complete image: ${result.imageRef}`);

            const pendingInfo = pendingRequestsRef.current.get(requestId);
            const settings =
              pendingInfo?.settings ?? toGenerationSnapshot(instanceConfigsRef.current[instanceId]);

            const newImage: GeneratedImage = {
              requestId,
              imageRef: result.imageRef,
              imageUrl: result.url,
              prompt: pendingInfo?.prompt || '',
              revisedPrompt: undefined,
              provider: settings.provider,
              model: settings.modelName,
              size: settings.size,
              quality: settings.quality,
              style: settings.style,
              enhancements: settings.enhancements,
              generatedAt: new Date(),
              instanceId,
              usedReferenceImage: !!settings.referenceImage,
              creativityPercent: settings.referenceImage
                ? Math.round((1 - (settings.referenceImage.strength ?? 0.5)) * 100)
                : undefined,
            };

            addImage(instanceId, newImage);

            updateInstanceState(instanceId, {
              stage: 'complete',
              progressMessage: 'Image generated successfully!',
              currentRequestId: null,
            });

            pendingRequestsRef.current.delete(requestId);
            pollAttemptsRef.current.delete(requestId);
            setTimeout(scrollAllInstancesToBottom, 100);
          }
        } catch (err) {
          console.debug('Polling check failed:', err);
        }
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [hasActiveRequests, scrollAllInstancesToBottom, updateInstanceState, addImage]);

  // Handle streaming completion for each instance (fallback)
  useEffect(() => {
    const completions = [
      {
        complete: streaming0Complete,
        requestId: instance0RequestId,
        reset: resetStreaming0,
        idx: 0,
      },
      {
        complete: streaming1Complete,
        requestId: instance1RequestId,
        reset: resetStreaming1,
        idx: 1,
      },
      {
        complete: streaming2Complete,
        requestId: instance2RequestId,
        reset: resetStreaming2,
        idx: 2,
      },
      {
        complete: streaming3Complete,
        requestId: instance3RequestId,
        reset: resetStreaming3,
        idx: 3,
      },
      {
        complete: streaming4Complete,
        requestId: instance4RequestId,
        reset: resetStreaming4,
        idx: 4,
      },
    ];

    for (const { complete, requestId, reset, idx } of completions) {
      if (complete && requestId) {
        const s = instanceStates[idx];
        if (s.stage !== 'complete' && s.stage !== 'idle' && s.stage !== 'error') {
          updateInstanceState(idx, {
            stage: 'complete',
            progressMessage: 'Generation complete',
            currentRequestId: null,
          });
          reset();
        }
      }
    }
  }, [
    streaming0Complete,
    instance0RequestId,
    resetStreaming0,
    streaming1Complete,
    instance1RequestId,
    resetStreaming1,
    streaming2Complete,
    instance2RequestId,
    resetStreaming2,
    streaming3Complete,
    instance3RequestId,
    resetStreaming3,
    streaming4Complete,
    instance4RequestId,
    resetStreaming4,
    instanceStates,
    updateInstanceState,
  ]);

  // Reset instance stages to idle after completion (errors persist until user dismisses).
  // Track which instances already have a pending reset to avoid re-setting on each render.
  const resetTimeoutsRef = useRef<Map<number, NodeJS.Timeout>>(new Map());

  useEffect(() => {
    instanceStates.forEach((s, idx) => {
      if (s.stage === 'complete' && !resetTimeoutsRef.current.has(idx)) {
        const t = setTimeout(() => {
          resetTimeoutsRef.current.delete(idx);
          updateInstanceState(idx, { stage: 'idle', progressMessage: '' });
        }, 1500);
        resetTimeoutsRef.current.set(idx, t);
      } else if (s.stage === 'idle' || s.stage === 'starting' || s.stage === 'generating') {
        // Clear any pending reset if instance has moved on
        const existing = resetTimeoutsRef.current.get(idx);
        if (existing) {
          clearTimeout(existing);
          resetTimeoutsRef.current.delete(idx);
        }
      }
    });
  }, [instanceStates, updateInstanceState]);

  // Check if any instance is currently generating
  const isAnyGenerating = useMemo(
    () =>
      instanceStates.some(
        (s, idx) =>
          enabledInstances[idx] &&
          s.stage !== 'idle' &&
          s.stage !== 'complete' &&
          s.stage !== 'error',
      ),
    [instanceStates, enabledInstances],
  );

  const isInstanceGenerating = useCallback(
    (idx: number) => {
      const s = instanceStates[idx];
      return s.stage !== 'idle' && s.stage !== 'complete' && s.stage !== 'error';
    },
    [instanceStates],
  );

  // Submit handler
  const handleSubmit = useCallback(
    async (inputValue: string) => {
      const trimmedInput = inputValue.trim();

      const anyIdle = instanceStates.some((s, idx) => enabledInstances[idx] && s.stage === 'idle');

      addBreadcrumb('image', `Submit attempt: hasInput=${!!trimmedInput}, anyIdle=${anyIdle}`);

      if (!trimmedInput || !anyIdle) {
        addBreadcrumb('image', `Submit blocked: empty=${!trimmedInput}, anyIdle=${anyIdle}`);
        return;
      }

      setTimeout(scrollAllInstancesToBottom, 100);

      const configsSnapshot = instanceConfigs.map((c) => ({ ...c }));

      const enabledIdleInstances = instanceStates
        .map((s, idx) => ({ state: s, idx }))
        .filter(
          ({ state: s, idx }) =>
            enabledInstances[idx] && !minimizedInstances[idx] && s.stage === 'idle',
        );

      // Top-level trace spanning all instances in this submission batch
      const providers = [
        ...new Set(enabledIdleInstances.map(({ idx }) => configsSnapshot[idx].provider)),
      ];
      const models = [
        ...new Set(enabledIdleInstances.map(({ idx }) => configsSnapshot[idx].modelName)),
      ];
      const submitTraceId = traceStart('imageGeneration', {
        provider: providers.join(','),
        model: models.join(','),
        instanceCount: enabledIdleInstances.length,
      });

      addBreadcrumb('image', 'Image generation started', {
        provider: providers.join(','),
        model: models.join(','),
        instanceCount: enabledIdleInstances.length,
      });

      let submitHasError = false;

      for (const { idx } of enabledIdleInstances) {
        const config = configsSnapshot[idx];
        // Freeze what this generation runs with, so a later settings edit cannot
        // rewrite the metadata recorded against the finished image.
        const snapshot = toGenerationSnapshot(config);
        const traceId = traceStart('image.generate', {
          instanceId: idx,
          size: snapshot.size,
          quality: snapshot.quality,
          style: snapshot.style,
        });

        try {
          updateInstanceState(idx, {
            stage: 'starting',
            progressMessage: `Starting ${getModelDisplayName(config.modelName)}...`,
            error: null,
          });

          setTimeout(scrollAllInstancesToBottom, 50);

          const enhancementParts = ENHANCEMENT_CATEGORIES.filter((cat) =>
            snapshot.enhancements[cat.key]?.trim(),
          ).map((cat) => `${cat.label}: ${snapshot.enhancements[cat.key].trim()}`);

          const fullPrompt =
            enhancementParts.length > 0
              ? `${trimmedInput}\n\n${enhancementParts.join('\n')}`
              : trimmedInput;

          const filteredProviderParams = filterProviderParamsForModel(
            snapshot.providerParams,
            snapshot.modelName,
            availableModels,
          );
          const paramDefs = getProviderParamsForModel(snapshot.modelName, availableModels);
          const { quality, style, remainingParams } = extractQualityStyleFromParams(
            filteredProviderParams,
            snapshot,
            paramDefs,
          );

          const finalProviderParams = { ...remainingParams };

          const actingHolderId = getSelectedVaultHolderId() ?? null;
          const response = await api.generateImage(
            ARCHE_ID,
            {
              prompt: fullPrompt,
              provider: snapshot.provider,
              model: snapshot.modelName,
              size: snapshot.size,
              quality,
              style,
              ...(Object.keys(finalProviderParams).length > 0
                ? { providerParams: finalProviderParams }
                : {}),
              ...(snapshot.referenceImage?.imageRef
                ? { referenceImageRef: snapshot.referenceImage.imageRef }
                : {}),
              ...(snapshot.referenceImage?.fileId
                ? { referenceImageFileId: snapshot.referenceImage.fileId }
                : {}),
              ...(snapshot.referenceImage?.strength !== undefined
                ? { referenceStrength: snapshot.referenceImage.strength }
                : {}),
              ...(snapshot.creativeStyle
                ? { creativeStyle: { styleId: snapshot.creativeStyle.styleId } }
                : {}),
            },
            { actingHolderId },
          );

          const requestId = response.requestId;

          pendingRequestsRef.current.set(requestId, {
            instanceId: idx,
            prompt: trimmedInput,
            settings: snapshot,
            actingHolderId,
          });

          updateInstanceState(idx, {
            stage: 'generating',
            progressMessage: `${getModelDisplayName(config.modelName)} is creating your image...`,
            currentRequestId: requestId,
          });

          setTimeout(scrollAllInstancesToBottom, 100);

          addBreadcrumb('image', `Instance ${idx} got requestId: ${requestId}`);
          traceEnd(traceId, { requestId, instanceId: idx });
        } catch (err) {
          submitHasError = true;
          traceError(traceId, err instanceof Error ? err : new Error(String(err)));
          updateInstanceState(idx, {
            stage: 'error',
            progressMessage: '',
            error: err instanceof Error ? err.message : 'Failed to generate image',
            currentRequestId: null,
          });
        }
      }

      // End top-level trace for the submission batch
      if (submitHasError) {
        traceError(submitTraceId, new Error('One or more instances failed during submission'));
      } else {
        traceEnd(submitTraceId, { status: 'success', instanceCount: enabledIdleInstances.length });
      }
    },
    [
      instanceStates,
      enabledInstances,
      instanceConfigs,
      minimizedInstances,
      scrollAllInstancesToBottom,
      updateInstanceState,
      getModelDisplayName,
    ],
  );

  const handleDownload = useCallback(async (image: GeneratedImage) => {
    try {
      const response = await fetch(image.imageUrl);
      const blob = await response.blob();
      // Infer extension from content-type, falling back to png
      const contentType = blob.type || 'image/png';
      const ext = contentType.split('/')[1]?.replace('jpeg', 'jpg') || 'png';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fias-image-${image.requestId}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download image:', err);
      window.open(image.imageUrl, '_blank');
    }
  }, []);

  const handleDelete = useCallback(
    (requestId: string, instanceId: number) => {
      removeImage(instanceId, requestId);
    },
    [removeImage],
  );

  const cancelInstance = useCallback(
    (idx: number) => {
      const s = instanceStatesRef.current[idx];
      if (
        s.currentRequestId &&
        s.stage !== 'idle' &&
        s.stage !== 'complete' &&
        s.stage !== 'error'
      ) {
        const reqId = s.currentRequestId;
        api.cancelImageGeneration(ARCHE_ID, reqId).catch((err) => {
          console.debug('Cancel request failed (may have already completed):', err);
        });
        pendingRequestsRef.current.delete(reqId);
        pollAttemptsRef.current.delete(reqId);
        updateInstanceState(idx, {
          stage: 'idle',
          progressMessage: '',
          currentRequestId: null,
          error: null,
        });
      }
    },
    [updateInstanceState],
  );

  const handleCancel = useCallback(() => {
    for (let idx = 0; idx < NUM_INSTANCES; idx++) {
      cancelInstance(idx);
    }
  }, [cancelInstance]);

  const handleCancelInstance = useCallback(
    (instanceId: number) => {
      cancelInstance(instanceId);
    },
    [cancelInstance],
  );

  return {
    handleSubmit,
    handleCancel,
    handleCancelInstance,
    handleDownload,
    handleDelete,
    isAnyGenerating,
    isInstanceGenerating,
    scrollAllInstancesToBottom,
    instanceScrollRefs,
  };
}
